import React from "react";
import { ThemeProvider, CssBaseline, Box } from "@mui/material";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { getTheme } from "./components/ThemeConfig";
import AppBarModern from "./components/AppBarModern";
import MainContentModern from "./components/MainContentModern";
import SettingsDrawer from "./components/SettingsDrawer";
import { ThemeContextProvider, useThemeMode } from "./contexts/ThemeContext";
import { SettingsDrawerContextProvider } from "./contexts/SettingsDrawerContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SignInForm } from "./components/auth/SignInForm";
import { SignUpForm } from "./components/auth/SignUpForm";
import { UserProfile } from "./components/auth/UserProfile";
import { DialogProvider } from "./contexts/DialogContext";
import { TargetProvider } from "./contexts/TargetContext";
import { UIProvider } from "./contexts/UIContext";

// Your Google Client ID from Google Cloud Console
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        Loading...
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" />;
  }

  return <>{children}</>;
};

function ThemedApp() {
  const { mode } = useThemeMode();
  const theme = getTheme(mode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
      >
        <AppBarModern />
        <SettingsDrawer />
        <Box sx={{ flexGrow: 1 }}>
          <Routes>
            <Route path="/login" element={<Navigate to="/signin" />} />
            <Route path="/signin" element={<SignInForm />} />
            <Route path="/signup" element={<SignUpForm />} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <MainContentModern />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <ThemeContextProvider>
          <SettingsDrawerContextProvider>
            <AuthProvider>
              <UIProvider>
                <DialogProvider>
                  <TargetProvider>
                    <ThemedApp />
                  </TargetProvider>
                </DialogProvider>
              </UIProvider>
            </AuthProvider>
          </SettingsDrawerContextProvider>
        </ThemeContextProvider>
      </GoogleOAuthProvider>
    </BrowserRouter>
  );
}

export default App;
